import React, { useContext, useEffect, useRef, useState } from 'react'
import { Collapse, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material'
import theme from '../../theme'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import AllSports from './AllSports'
import StarIcon from '@mui/icons-material/Star'
import SearchIcon from '@mui/icons-material/Search'
import SupportIcon from '@mui/icons-material/SupportAgent'
import BoardIcon from '@mui/icons-material/GridView'
import SportsBaseballIcon from '@mui/icons-material/SportsBaseball'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import TuneIcon from '@mui/icons-material/Tune'
import NotificationsIcon from '@mui/icons-material/NotificationsActive'
import NotificationsSettingsIcon from '@mui/icons-material/EditNotifications'
import GroupLeagues from './GroupLeagues'
import Context from '../../Context'
import { findArbs } from '../../utils/arb'
import { findValueBets } from '../../utils/valueBets'
import { leagueKey } from '../../utils/settings'
import { apiFetch } from '../../utils/api'
import findInitialLeague from '../../utils/findInitialLeague'
import { getSessionId } from '../../utils/session'

function MenuGroups() {
  const [ openGroup, setOpenGroup ] = useState(null)
  const [ openSport, setOpenSport ] = useState(null)
  const [ valueLeagues, setValueLeagues ] = useState([])
  const [ arbLeagues, setArbLeagues ] = useState([])
  const [ menuLoading, setMenuLoading ] = useState(true)
  const [ allSports, setAllSports ] = useState([])
  const { currentLeague, setCurrentLeague, settings, setKnownLeagues, polledHost, setIsMenuLoading, selectedBookmaker, setSelectedEvent, showSettings, setShowSettings, showNotifications, setShowNotifications, showNotificationSettings, setShowNotificationSettings, setInitialLeague, showSearch, setShowSearch, showSupport, setShowSupport, showBoard, setShowBoard } = useContext(Context)
  const sessionId = getSessionId()
  // only the newest menu request may paint; the effect fires twice per load by design
  const menuSeq = useRef(0)

  useEffect(() => {
    const fetchData = async () => {
      setMenuLoading(true)
      try {
        await fetchAllSports()
        setIsMenuLoading(false)
      } catch (error) {
        console.error('Error loading menu:', error)
        alert('Error loading menu, try reloading the page!')
        setIsMenuLoading(false)
      } finally {
        setMenuLoading(false)
      }
    }
    fetchData()
    // both group lists are derived from the thresholds, so a change to either has to rebuild the menu
    // eslint-disable-next-line
  }, [selectedBookmaker, settings.minYield, settings.minArbProfit, settings.hiddenSports, settings.hiddenLeagues])

  const fetchAllSports = async () => {
    const seq = ++menuSeq.current
    const url = `/odds-comparison01?sessionId=${sessionId}&addAll=false&favOnly=false&minYield=-1&bookmaker=${selectedBookmaker}`

    const response = await apiFetch(polledHost, url)

    const data = await response.json()

    if (seq !== menuSeq.current) {
      return
    }

    const sportsMap = new Map()

    data.forEach((item) => {
      const { sport: sportName, leagues: leaguesArr } = item

      leaguesArr.forEach((league) => {
        const { league: leagueName, eventList } = league
        const numberOfEvents = eventList.length

        let currentValue

        if (sportsMap.has(sportName)) {
          currentValue = sportsMap.get(sportName)
        } else {
          currentValue = new Map()
        }

        currentValue.set(leagueName, numberOfEvents)
        sportsMap.set(sportName, currentValue)
      })
    })

    const initialLeague = findInitialLeague(sportsMap)
    setInitialLeague(initialLeague)

    /**
     * Expanding the tree is part of "you are here", so it only applies where the menu is allowed to pick
     * where here is — the bare address. Doing it unconditionally left Odds Comparison and Baseball open
     * in the sidebar while the screen showed the notifications feed.
     */
    if (window.location.pathname === '/') {
      const { group, sport } = initialLeague
      setOpenGroup(group)
      setOpenSport(sport)
    }

    // the counts were already being computed and thrown away, the menu just never showed them
    const everything = Array.from(sportsMap)
      .map(([sportName, leaguesMap]) => [
        sportName,
        [...leaguesMap].map(([league, count]) => ({ league, count })).sort((a, b) => a.league.localeCompare(b.league))
      ])
      .sort((a, b) => a[0].localeCompare(b[0]))

    setKnownLeagues(everything)
    setAllSports(everything
      .filter(([sportName]) => !settings.hiddenSports.includes(sportName))
      .map(([sportName, leagues]) => [
        sportName,
        leagues.filter((entry) => !settings.hiddenLeagues.includes(leagueKey(sportName, entry.league)))
      ])
      .filter(([, leagues]) => leagues.length))

    // Value bets and arbs are answered off this same payload rather than a second request: the arb list
    // in particular has to be built from real arbs, otherwise it offers leagues that filter down to nothing.
    const value = []
    const arbs = []
    data.forEach((sportItem) => {
      (sportItem.leagues || []).forEach((leagueItem) => {
        const events = leagueItem.eventList || []
        if (settings.hiddenSports.includes(sportItem.sport)
            || settings.hiddenLeagues.includes(leagueKey(sportItem.sport, leagueItem.league))) {
          return
        }
        const entry = { sport: sportItem.sport, league: leagueItem.league }
        const valueCount = events.filter((event) => findValueBets(event, settings.minYield).length).length
        const arbCount = events.filter((event) => findArbs(event, settings.minArbProfit).length).length
        if (valueCount) {
          value.push({ ...entry, count: valueCount })
        }
        if (arbCount) {
          arbs.push({ ...entry, count: arbCount })
        }
      })
    })
    // busiest first: the point of these two lists is where something is happening, not the alphabet
    const byCount = (a, b) => b.count - a.count || a.sport.localeCompare(b.sport) || a.league.localeCompare(b.league)
    setValueLeagues(value.sort(byCount))
    setArbLeagues(arbs.sort(byCount))
  }

  /**
   * Arriving by link or by Back has to move the highlight too, otherwise the tree keeps pointing at
   * wherever it was last opened by hand while the screen shows something else.
   */
  useEffect(() => {
    if (currentLeague.group) {
      setOpenGroup(currentLeague.group)
      setOpenSport(currentLeague.sport)
    } else if (showSettings || showNotifications || showNotificationSettings || showSearch || showSupport || showBoard) {
      setOpenGroup(null)
      setOpenSport(null)
    }
  }, [currentLeague.group, currentLeague.sport, showSettings, showNotifications, showNotificationSettings, showSearch, showSupport, showBoard])

  const handleToggle = (name) => {
    setSelectedEvent(null)
    setShowSettings(false)
    setOpenGroup((prevOpenItem) => (prevOpenItem === name ? null : name))
    setOpenSport(null)

    if (name === 'Favorites') {
      setCurrentLeague({ group: 'Favorites', sport: null, league: 'Favorites' })
    }
  }

  return(
    <List>
      <ListItem key='Favorites' disablePadding>
        <ListItemButton
          onClick={() => handleToggle('Favorites')}
          sx={{
            '&:hover': {
              backgroundColor: theme.palette.secondary.main,
            },
            backgroundColor: openGroup === 'Favorites' ? theme.palette.secondary.main : theme.palette.primary.main,
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 'auto', marginRight: '10px' }}><StarIcon /></ListItemIcon>
          <ListItemText sx={{ color: 'white' }} primary={'Favorites'} />
        </ListItemButton>
      </ListItem>
      {settings.favoritesBoard !== false && (
      <ListItem key='Board' disablePadding>
        <ListItemButton
          onClick={() => {
            setSelectedEvent(null)
            setOpenGroup(null)
            setShowBoard(true)
          }}
          sx={{
            '&:hover': {
              backgroundColor: theme.palette.secondary.main,
            },
            backgroundColor: showBoard ? theme.palette.secondary.main : theme.palette.primary.main,
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 'auto', marginRight: '10px' }}><BoardIcon /></ListItemIcon>
          <ListItemText sx={{ color: 'white' }} primary={'Fav compact v2'} />
        </ListItemButton>
      </ListItem>
      )}
      <ListItem key='Search' disablePadding>
        <ListItemButton
          onClick={() => {
            setSelectedEvent(null)
            setOpenGroup(null)
            setShowSearch(true)
          }}
          sx={{
            '&:hover': {
              backgroundColor: theme.palette.secondary.main,
            },
            backgroundColor: showSearch ? theme.palette.secondary.main : theme.palette.primary.main,
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 'auto', marginRight: '10px' }}><SearchIcon /></ListItemIcon>
          <ListItemText sx={{ color: 'white' }} primary={'Search'} />
        </ListItemButton>
      </ListItem>
      <ListItem key='All Sports' disablePadding>
        <ListItemButton
          onClick={() => handleToggle('All Sports')}
          sx={{
            '&:hover': {
              backgroundColor: theme.palette.secondary.main,
            },
            backgroundColor: openGroup === 'All Sports' ? theme.palette.secondary.main : theme.palette.primary.main,
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 'auto', marginRight: '10px' }}><SportsBaseballIcon /></ListItemIcon>
          {/* the group key stays "All Sports", it is compared against in Odds.js and findInitialLeague */}
          <ListItemText sx={{ color: 'white' }} primary={'Odds Comparison'} />
          {openGroup === 'All Sports' ? <ExpandLess sx={{ color: 'white' }} /> : <ExpandMore sx={{ color: 'white' }} />}
        </ListItemButton>
      </ListItem>
      <Collapse in={openGroup === 'All Sports'} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <AllSports sports={allSports} openSport={openSport} setOpenSport={setOpenSport} />
        </List>
      </Collapse>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.35)', marginY: '6px' }} />

      <ListItem key='Arbs' disablePadding>
        <ListItemButton
          onClick={() => handleToggle('Arbs')}
          sx={{
            '&:hover': {
              backgroundColor: theme.palette.secondary.main,
            },
            backgroundColor: openGroup === 'Arbs' ? theme.palette.secondary.main : theme.palette.primary.main,
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 'auto', marginRight: '10px' }}><CompareArrowsIcon /></ListItemIcon>
          <ListItemText sx={{ color: 'white' }} primary={'Arbs'} />
          {openGroup === 'Arbs' ? <ExpandLess sx={{ color: 'white' }} /> : <ExpandMore sx={{ color: 'white' }} />}
        </ListItemButton>
      </ListItem>
      <Collapse in={openGroup === 'Arbs'} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <GroupLeagues groupName={'Arbs'} entries={arbLeagues} loading={menuLoading} emptyText={`No arbs at ${settings.minArbProfit}% or better right now.`} />
        </List>
      </Collapse>
      <ListItem key='Discrepancies' disablePadding>
        <ListItemButton
          onClick={() => handleToggle('Discrepancies')}
          sx={{
            '&:hover': {
              backgroundColor: theme.palette.secondary.main,
            },
            backgroundColor: openGroup === 'Discrepancies' ? theme.palette.secondary.main : theme.palette.primary.main,
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 'auto', marginRight: '10px' }}><TrendingUpIcon /></ListItemIcon>
          <ListItemText sx={{ color: 'white' }} primary={'Value Bets'} />
          {openGroup === 'Discrepancies' ? <ExpandLess sx={{ color: 'white' }} /> : <ExpandMore sx={{ color: 'white' }} />}
        </ListItemButton>
      </ListItem>
      <Collapse in={openGroup === 'Discrepancies'} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <GroupLeagues groupName={'Discrepancies'} entries={valueLeagues} loading={menuLoading} emptyText={`No value bets at ${settings.minYield}% or better right now.`} />
        </List>
      </Collapse>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.35)', marginY: '6px' }} />

      <ListItem key='Notifications' disablePadding>
        <ListItemButton
          onClick={() => {
            setSelectedEvent(null)
            setOpenGroup(null)
            setShowNotifications(true)
          }}
          sx={{
            '&:hover': {
              backgroundColor: theme.palette.secondary.main,
            },
            backgroundColor: showNotifications ? theme.palette.secondary.main : theme.palette.primary.main,
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 'auto', marginRight: '10px' }}><NotificationsIcon /></ListItemIcon>
          <ListItemText sx={{ color: 'white' }} primary={'Arbs notifications'} primaryTypographyProps={{ sx: { fontSize: '13px' } }} />
        </ListItemButton>
      </ListItem>

      <ListItem key='NotificationSettings' disablePadding>
        <ListItemButton
          onClick={() => {
            setSelectedEvent(null)
            setOpenGroup(null)
            setShowNotificationSettings(true)
          }}
          sx={{
            '&:hover': {
              backgroundColor: theme.palette.secondary.main,
            },
            backgroundColor: showNotificationSettings ? theme.palette.secondary.main : theme.palette.primary.main,
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 'auto', marginRight: '10px' }}><NotificationsSettingsIcon /></ListItemIcon>
          <ListItemText sx={{ color: 'white' }} primary={'Notification settings'} primaryTypographyProps={{ sx: { fontSize: '13px' } }} />
        </ListItemButton>
      </ListItem>


      <ListItem key='Settings' disablePadding>
        <ListItemButton
          onClick={() => {
            setSelectedEvent(null)
            setOpenGroup(null)
            setShowSettings(true)
          }}
          sx={{
            '&:hover': {
              backgroundColor: theme.palette.secondary.main,
            },
            backgroundColor: showSettings ? theme.palette.secondary.main : theme.palette.primary.main,
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 'auto', marginRight: '10px' }}><TuneIcon /></ListItemIcon>
          <ListItemText sx={{ color: 'white' }} primary={'Settings'} />
        </ListItemButton>
      </ListItem>
      <ListItem key='Support' disablePadding>
        <ListItemButton
          onClick={() => {
            setSelectedEvent(null)
            setOpenGroup(null)
            setShowSupport(true)
          }}
          sx={{
            '&:hover': {
              backgroundColor: theme.palette.secondary.main,
            },
            backgroundColor: showSupport ? theme.palette.secondary.main : theme.palette.primary.main,
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 'auto', marginRight: '10px' }}><SupportIcon /></ListItemIcon>
          <ListItemText sx={{ color: 'white' }} primary={'Support'} />
        </ListItemButton>
      </ListItem>

    </List>
  )
}

export default MenuGroups